import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { create, update } from 'apis/programs.api';
import createProgramValidationSchema from 'features/Program/validation';
import { DatePicker, Select, TextareaInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { getEndingTimeOfDate, getTemplatePrograms, mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createProgramValidationSchema) };

const Form = forwardRef(({ refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [programControl, setProgramControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({ ...formOptions, mode: 'onChange' });

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(create);
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(update);

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_programControl) => {
        setProgramControl(_programControl);
        setVisible(true);
        if (_programControl.mode === 'CREATE') {
          reset({});
        } else if (_programControl.mode === 'UPDATE') {
          const {
            startingTime,
            endingTime,
            organizationPrequalificationDeadline,
            staffPrequalificationDeadline,
            ...rest
          } = _programControl.value;

          reset({
            ...rest,
            startingTime: startingTime ? new Date(startingTime) : null,
            endingTime: endingTime ? new Date(endingTime) : null,
            organizationPrequalificationDeadline: organizationPrequalificationDeadline
              ? new Date(organizationPrequalificationDeadline)
              : null,
            staffPrequalificationDeadline: staffPrequalificationDeadline
              ? new Date(staffPrequalificationDeadline)
              : null,
          });
        }
      },
    }),
    []
  );

  const onSubmit = (data) => {
    const startingTime = data?.startingTime ? new Date(data.startingTime) : null;
    const endingTime = data?.endingTime ? getEndingTimeOfDate(new Date(data.endingTime)) : null;
    const organizationPrequalificationDeadline = data?.organizationPrequalificationDeadline
      ? getEndingTimeOfDate(new Date(data.organizationPrequalificationDeadline))
      : null;
    const staffPrequalificationDeadline = data?.staffPrequalificationDeadline
      ? getEndingTimeOfDate(new Date(data.staffPrequalificationDeadline))
      : null;

    if (programControl.mode === 'CREATE') {
      const newData = {
        ...data,
        startingTime,
        endingTime,
        staffPrequalificationDeadline,
        organizationPrequalificationDeadline,
      };

      mutateCreate(newData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('program.create.createSuccess'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const { id, ...rest } = {
        ...data,
        startingTime,
        endingTime,
        organizationPrequalificationDeadline,
        staffPrequalificationDeadline,
      };
      const newData = Object.keys(dirtyFields)?.reduce((resultObj, current) => {
        // eslint-disable-next-line no-param-reassign
        resultObj[current] = rest[current];
        return resultObj;
      }, {});

      mutateUpdate(
        { id, data: newData },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('program.update.updateSuccess'));
            refetch();
            setVisible(false);
          },
          onError: (error) => {
            toast(TOAST.ERROR, mapError(t, error));
          },
        }
      );
    }
  };

  const handleDeadlineTimeChange = async () => {
    await trigger('startingTime');
    await trigger('endingTime');
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const templateOptions = useMemo(() => getTemplatePrograms(), []);
  // #endregion

  return (
    <Dialog
      header={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <p className="text-blue-500">
          {programControl?.mode === 'CREATE'
            ? t('program.create.title')
            : t('program.update.title')}
        </p>
      }
      visible={visible}
      style={{ maxWidth: '500px' }}
      onHide={() => setVisible(false)}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-12">
              <TextareaInput
                label={t('program.name')}
                name="name"
                autoFocus
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-12 mb-2">
              <TextareaInput
                label={t('program.description')}
                name="description"
                control={control}
                errors={errors}
                row={2}
                isRequired
              />
            </div>
            <div className="col-6">
              <DatePicker
                label={t('program.startingTime')}
                name="startingTime"
                control={control}
                errors={errors}
                isRequired
                onCustomChange={handleDeadlineTimeChange}
              />
            </div>
            <div className="col-6">
              <DatePicker
                label={t('program.endingTime')}
                name="endingTime"
                control={control}
                errors={errors}
                isRequired
                onCustomChange={handleDeadlineTimeChange}
              />
            </div>

            <div className="col-6">
              <DatePicker
                label={t('program.organizationPrequalificationDeadline')}
                name="organizationPrequalificationDeadline"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-6">
              <DatePicker
                label={t('program.staffPrequalificationDeadline')}
                name="staffPrequalificationDeadline"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-6">
              <Select
                label={t('program.template')}
                name="template"
                options={templateOptions}
                inputField="value"
                renderField="name"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('program.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={programControl?.mode === 'CREATE' ? t('program.add') : t('program.save')}
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={!isDirty || !(Object.keys(dirtyFields).length > 0)}
              />
            </div>
          </div>
        </form>
        {(isLoadingCreate || isLoadingUpdate) && <Loading />}
      </div>
    </Dialog>
  );
});

export default Form;

Form.propTypes = {
  refetch: PropTypes.func.isRequired,
};
